import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from '../utils/api';
import toast from 'react-hot-toast';

const MasterRecordsContext = createContext();

export const useMasterRecords = () => {
  const context = useContext(MasterRecordsContext);
  if (!context) {
    throw new Error('useMasterRecords must be used within a MasterRecordsProvider');
  }
  return context;
};

export const MasterRecordsProvider = ({ children }) => {
  const [masterRecords, setMasterRecords] = useState({
    STATUS: [],
    SOURCE: [],
    PRIORITY: [],
    ASSIGNED_TO: [],
    SERVICE_INTEREST: []
  });
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchMasterRecords = useCallback(async () => {
    try {
      setLoading(true);
      const [statusResponse, sourceResponse, priorityResponse, assignedResponse, serviceResponse] = await Promise.all([
        axios.get('/api/master-records/type/STATUS').catch(() => ({ data: [] })),
        axios.get('/api/master-records/type/SOURCE').catch(() => ({ data: [] })),
        axios.get('/api/master-records/type/PRIORITY').catch(() => ({ data: [] })),
        axios.get('/api/master-records/type/ASSIGNED_TO').catch(() => ({ data: [] })),
        axios.get('/api/master-records/type/SERVICE_INTEREST').catch(() => ({ data: [] }))
      ]);

      const newRecords = {
        STATUS: statusResponse.data || [],
        SOURCE: sourceResponse.data || [],
        PRIORITY: priorityResponse.data || [],
        ASSIGNED_TO: assignedResponse.data || [],
        SERVICE_INTEREST: serviceResponse.data || []
      };

      // Sort records by sortOrder
      Object.keys(newRecords).forEach(type => {
        newRecords[type].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
      });

      setMasterRecords(newRecords);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error fetching master records:', error);
      toast.error('Failed to fetch master records');
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshMasterRecords = useCallback(() => {
    return fetchMasterRecords();
  }, [fetchMasterRecords]);

  const getRecordColor = useCallback((type, name) => {
    const record = masterRecords[type]?.find(r => r.name === name);
    return record?.colorCode || null;
  }, [masterRecords]);

  const getRecordDisplayName = useCallback((type, name) => {
    const record = masterRecords[type]?.find(r => r.name === name);
    return record?.displayName || name;
  }, [masterRecords]);

  const getRecordDescription = useCallback((type, name) => {
    const record = masterRecords[type]?.find(r => r.name === name);
    return record?.description || '';
  }, [masterRecords]);

  const isRecordEditable = useCallback((type, name) => {
    // CONVERTED status should not be editable
    if (type === 'STATUS' && name === 'CONVERTED') {
      return false;
    }
    return true;
  }, []);

  const getStatusColor = useCallback((status) => {
    const colorCode = getRecordColor('STATUS', status);
    if (colorCode) {
      return `bg-[${colorCode}]/10 text-[${colorCode}] dark:bg-[${colorCode}]/20 dark:text-[${colorCode}]/80`;
    }
    
    // Fallback colors
    switch (status) {
      case 'NEW':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
      case 'CONTACTED':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300';
      case 'QUALIFIED':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
      case 'CONVERTED':
        return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-300';
      case 'LOST':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  }, [getRecordColor]);

  const getSourceColor = useCallback((source) => {
    return getRecordColor('SOURCE', source) || '#3B82F6';
  }, [getRecordColor]);

  const generateChartColors = useCallback((type, data) => {
    const defaultColors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];
    return data.map((item, index) => {
      const record = masterRecords[type]?.find(r => r.name === item.name);
      return record?.colorCode || defaultColors[index % defaultColors.length];
    });
  }, [masterRecords]);

  useEffect(() => {
    fetchMasterRecords();
  }, [fetchMasterRecords]);

  // Expose global refresh function
  useEffect(() => {
    window.refreshMasterRecords = refreshMasterRecords;
    return () => {
      delete window.refreshMasterRecords;
    };
  }, [refreshMasterRecords]);

  const value = {
    masterRecords,
    loading,
    lastUpdated,
    refreshMasterRecords,
    getRecordColor,
    getRecordDisplayName,
    getRecordDescription,
    isRecordEditable,
    getStatusColor,
    getSourceColor,
    generateChartColors
  };

  return (
    <MasterRecordsContext.Provider value={value}>
      {children}
    </MasterRecordsContext.Provider>
  );
};



